import { WarningCircleIcon } from '@phosphor-icons/react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SessionState } from '@entities/search-session';
import { Avatar } from '@shared/ui';

import { chatErrorKey } from '../model/errors';
import type { ChatDraft } from '../model/useChatDraft';
import { useQuickReplyDraft } from '../model/useQuickReplyDraft';

import styles from './Chat.module.css';
import { ChatComposer } from './ChatComposer';
import { ChatMessageItem, KarlGreeting } from './ChatMessageItem';
import { QuickReplies } from './QuickReplies';

interface ChatPanelProps {
  session: SessionState;
  draft: ChatDraft;
  maxLength: number;
  /** Идёт обработка сообщения: ввод заблокирован, показывается индикатор. */
  sending: boolean;
  sendError: unknown;
  onSend: (content: string, quickReplyCodes?: string[]) => void;
  /** Резервная форма (FE-072). Показывается вместо ответа, когда LLM недоступен. */
  fallbackSlot?: ReactNode | undefined;
  /**
   * Действие над полем ввода — например, запуск подбора на узком экране, где
   * панели критериев рядом нет (FE-595). Видимость по ширине решает страница.
   */
  actionSlot?: ReactNode | undefined;
  /**
   * `panel` — самостоятельная панель на экране диалога.
   * `rail` — та же лента внутри рельса Karl: без собственной рамки, потому что
   * рельс уже рисует карточку. Карточка внутри карточки — дефект вёрстки
   * (DESIGN_SYSTEM §5.2).
   *
   * Отдельной шапки с личностью Karl нет ни в одном варианте: имя, фото и
   * маркировка «KI-Assistent» стоят у каждой его реплики (DESIGN_SYSTEM §5.4),
   * и шапка над ними повторяла бы то же самое ещё раз.
   */
  variant?: 'panel' | 'rail' | undefined;
}

/**
 * Диалог с консультантом (FE-070, MVP_PRD §9).
 *
 * Панель отвечает за ленту, ввод и состояния ожидания. Всё остальное —
 * не её дело: критерии считает backend, следующий вопрос выбирает планировщик,
 * тексты собираются из ключей.
 *
 * Лента объявлена `role="log"` с `aria-live="polite"`: ответ консультанта
 * приходит асинхронно, и скринридер обязан его получить, не отбирая фокус у
 * поля ввода.
 *
 * **Кнопки повтора нет** (FE-582). Сообщение, не дошедшее до сервера,
 * откатывается из ленты, а набранный текст возвращается в поле — ошибка
 * показана баннером. Вторая копия того же текста в ленте с «Erneut senden»
 * означала бы два способа отправить одно и то же.
 */
export function ChatPanel({
  session,
  draft,
  maxLength,
  sending,
  sendError,
  onSend,
  fallbackSlot,
  actionSlot,
  variant = 'panel',
}: ChatPanelProps) {
  const { t } = useTranslation();
  const threadRef = useRef<HTMLDivElement>(null);

  const messages = session.messages;
  const question = session.criteria.next_question;
  const replies = useQuickReplyDraft(question, draft);
  // Признак, а не сам узел: элемент пересоздаётся каждый рендер, и прокрутка
  // запускалась бы на каждый штрих в поле ввода.
  const hasFallback = fallbackSlot != null;

  // Лента прокручивается к последней реплике: и после ответа, и во время
  // ожидания — индикатор набора тоже должен быть виден.
  useEffect(() => {
    const node = threadRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
  }, [messages.length, sending, hasFallback]);

  const submit = (content: string, quickReplyCodes?: string[]) => {
    const trimmed = content.trim();
    if (trimmed === '' || sending) return;
    onSend(trimmed, quickReplyCodes);
  };

  const rail = variant === 'rail';

  return (
    <section
      className={[styles.panel, rail && styles.panelRail].filter(Boolean).join(' ')}
      aria-label={t('chat.title')}
    >
      <div className={styles.thread} ref={threadRef} role="log" aria-live="polite">
        <ul className={styles.turns}>
          {messages.length === 0 ? (
            <KarlGreeting compact={rail} />
          ) : (
            messages.map((message) => (
              <ChatMessageItem
                key={message.client_message_id ?? message.id}
                message={message}
                compact={rail}
              />
            ))
          )}

          {sending ? (
            <li className={styles.turnAssistant}>
              {rail ? null : <Avatar variant="karl" size="md" />}
              <p className={styles.typing} role="status">
                <span className={styles.dots} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                {t('chat.typing')}
              </p>
            </li>
          ) : null}
        </ul>

        {fallbackSlot ? <div className={styles.fallbackSlot}>{fallbackSlot}</div> : null}
      </div>

      <div className={styles.footer}>
        {sendError && !sending ? (
          <div className={styles.error} role="alert">
            <WarningCircleIcon size={18} weight="fill" aria-hidden="true" />
            <span>{t(chatErrorKey(sendError))}</span>
          </div>
        ) : null}

        {actionSlot}

        {question ? (
          <QuickReplies
            question={question}
            disabled={sending}
            active={replies.active}
            onToggle={replies.toggle}
          />
        ) : null}

        <ChatComposer
          value={draft.value}
          maxLength={maxLength}
          disabled={sending}
          onChange={draft.set}
          onSubmit={() => {
            // Коды — только подсказок, чьи подписи остались в тексте: backend
            // применит их без модели, а остальное разберёт как обычный текст.
            submit(draft.value, replies.active.length > 0 ? replies.active : undefined);
          }}
        />
      </div>
    </section>
  );
}
